import React, { useEffect, useRef, useCallback, useMemo, useState } from 'react';
import qs from 'qs';
import Space, { LoaderSpace } from '../../../components/xr/space';
import { CoordsSelectSpace } from '../space';
import { updateAppState, setLoadedSpace, setSpace } from '../../../store/actions/app';

import GatewayService from '../../../services/gateway-service';
import SocketService from '../../../services/socket-service';
import PermissionService from '../../../services/permission-service';

import * as GatewayEvents from '../../../common/constants/gateway-events';
import * as SocketEvents from '../../../common/constants/socket-events';
import Roles from '../../../common/constants/roles';
import { useHistory, useLocation } from 'react-router-dom';
import { useActions, useSocket, useRefQueue } from '../../../hooks';
import store from '../../../config/redux-store';
import Stick from '../stick';
import { useFrame, useThree } from 'react-three-fiber';
import { PerspectiveCamera } from 'drei';
import { LookControls } from '../../../components/xr/controls';
import LookControlsImpl from '../../../common/three/look-controls';
import { Euler, NoToneMapping, MathUtils, Group, LinearEncoding, TextureEncoding } from 'three';
import Loader from '../../../components/xr/loader';
import { isEmpty, isEqual } from 'lodash';

import { context } from '../../../config/context';
import { isMacLike } from '../../../common/device';
import { useSelector } from 'react-redux';
import { RootState } from '../../../types/data';

const { degToRad } = MathUtils;

let prevRotation = {
  x: 0,
  y: 0,
  z: 0,
};

let frameNumber = 0;
type TMainEnvironment = React.Props<{}> & {};

type TMainEnvironmentReduxState = {
  space: RootState['app']['space'];
  appState: RootState['app']['state'];
};

const MainEnvironment = ({ children }: TMainEnvironment) => {
  const { gl } = useThree();
  const {
    space: {
      current: newSpace,
      loaded: spaceLoaded,
      history: [prevSpace, currSpace],
    },
    appState: {
      coordsSelectEnabled,
      isStickEnabled,
      isCameraSyncEnabled,
      isContentVisible,
      showLoader,
      spotLookAt,
      lookAt,
    },
  } = useSelector<RootState, TMainEnvironmentReduxState>(state => ({
    space: state.app.space,
    appState: state.app.state,
  }));
  const { search, pathname } = useLocation();
  const browserHistory = useHistory();
  const { socket_room } = qs.parse(search, { ignoreQueryPrefix: true });
  const Socket = useSocket(!!socket_room);
  const controlsRef = useRef<LookControlsImpl>();
  const mainRef = useRef<Group>();
  const loaderSpcContRef = useRefQueue<Group>();
  const loaderSpcRef = useRefQueue<LoaderSpace>();
  const [state, setState] = useState<{ isInitialUpdate: boolean }>({
    isInitialUpdate: true,
  });

  const boundActions = useActions({
    updateAppState,
    setLoadedSpace,
    setSpace,
  });

  const handleTick = useCallback(() => {
    // console.log(delta);
    if (controlsRef.current) {
      // get camera rotation in each frame
      const rotation = controlsRef.current.object3D.rotation;

      const data = {
        value: {
          x: rotation.x,
          y: rotation.y,
          z: rotation.z,
        },
      };

      if (!isEqual(data.value, prevRotation)) {
        // check if user is presenter emit that data to SS
        if (
          isCameraSyncEnabled &&
          PermissionService.hasRoles([Roles.ROLE_PRESENTER]) &&
          Socket &&
          ++frameNumber % 10 === 0
        ) {
          frameNumber = 0;
          Socket.emit(SocketEvents.SYNC_CAMERA, data);
        }

        // eit camera rotation with GatewayService for 2D animations
        GatewayService.emit(GatewayEvents.SYNC_CAMERA_ROTATION, data);
      }

      prevRotation = { ...data.value };
    }
  }, [Socket, isCameraSyncEnabled]);

  useFrame(handleTick);

  useEffect(() => {
    const handleCoordsEnableEvt = () => boundActions.updateAppState({ coordsSelectEnabled: true });
    const handleContentVisibilityEvt = (data: any) => boundActions.updateAppState({ isContentVisible: data.value });
    const handleLocationChange = ({ value }: any) => {
      try {
        const { pathname } = new URL(value);

        browserHistory.push({
          pathname: pathname,
          search: window.location.search,
        });
      } catch (e) {
        console.error('log: error: handleSyncLocation', e, value);
      }
    };

    GatewayService.on(GatewayEvents.ACTION_LOCATION_CHANGE, handleLocationChange);
    GatewayService.on(GatewayEvents.ACTION_ENABLE_COORDS_SELECT_MODE, handleCoordsEnableEvt);
    GatewayService.on(GatewayEvents.ACTION_TOGGLE_CONTENT_VISIBILITY, handleContentVisibilityEvt);

    return () => {
      GatewayService.off(GatewayEvents.ACTION_LOCATION_CHANGE, handleLocationChange);
      GatewayService.off(GatewayEvents.ACTION_ENABLE_COORDS_SELECT_MODE, handleCoordsEnableEvt);
      GatewayService.off(GatewayEvents.ACTION_ENABLE_COORDS_SELECT_MODE, handleContentVisibilityEvt);
    };
  }, [boundActions, browserHistory]);

  // subscribe camera rotation updates if user is participant and sync enabled
  useEffect(() => {
    if (!PermissionService.hasRoles([Roles.ROLE_PARTICIPANT]) || !controlsRef.current || !Socket) return;

    const handleCameraSync = ({ data }: any) => {
      controlsRef.current?.lookAtWithAnimation(data.value);
    };

    if (isCameraSyncEnabled) {
      controlsRef.current.data.magicWindowTrackingEnabled = false;
      controlsRef.current.data.touchEnabled = false;
      controlsRef.current.data.mouseEnabled = false;
      Socket.on(SocketEvents.SYNC_CAMERA, handleCameraSync);
    } else {
      controlsRef.current.data.magicWindowTrackingEnabled = true;
      controlsRef.current.data.touchEnabled = true;
      controlsRef.current.data.mouseEnabled = true;
    }

    return () => {
      Socket?.off(SocketEvents.SYNC_CAMERA, handleCameraSync);
    };
  }, [Socket, isCameraSyncEnabled]);

  useEffect(() => {
    if (!Socket) return;

    // handler functions
    const handleStateSync = ({ data: { isStickEnabled } }: any) => {
      const isCameraSyncEnabled = store.getState().app.state.isCameraSyncEnabled;

      boundActions.updateAppState({
        isStickEnabled,
        isCameraSyncEnabled: isCameraSyncEnabled
          ? isCameraSyncEnabled
          : PermissionService.hasRoles([Roles.ROLE_PRESENTER]),
      });
    };

    const handleStateGet = (payload: any) => {
      const {
        data: { currentUrl },
      } = payload;

      if (currentUrl) {
        browserHistory.push({
          pathname: currentUrl,
          search: search,
        });
      }

      handleStateSync(payload);
    };

    const handleSyncLocation = ({ value }: any) => {
      try {
        const { pathname } = new URL(value);

        browserHistory.push({
          pathname: pathname,
          search: search,
        });
      } catch (e) {
        console.error('log: error: handleSyncLocation', e, value);
        console.log('log: debug: Please provide correct url.');
      }
    };

    const handleGatewayToggleStick = (data: any) => Socket?.emit(SocketEvents.ACTION_TOGGLE_STICK, data);
    const handleGatewayToggleCameraSync = (data: any) => {
      // if event emitted by PRESENTER toggle session camera sync state
      if (PermissionService.hasRoles([Roles.ROLE_PRESENTER])) {
        // Socket?.emit(SocketEvents.ACTION_TOGGLE_CAMERA_SYNC, data);
      } else {
        // if event emitted by participant toggle only his camera sync state
        // add check for prevent event cycling
        const isCameraSyncEnabled = store.getState().app.state.isCameraSyncEnabled;
        if (isCameraSyncEnabled === data.value) {
          return;
        }

        boundActions.updateAppState({ isCameraSyncEnabled: data.value });
        GatewayService.emit(GatewayEvents.ACTION_TOGGLE_CAMERA_SYNC, data);
      }
    };

    // get session state from SS
    Socket.emit(SocketEvents.SYNC_GET_STATE, handleStateGet);
    // subscribe session state updates
    Socket.on(SocketEvents.SYNC_UPDATE_STATE, handleStateSync);
    // subscribe session to GatewayService events
    GatewayService.on(GatewayEvents.SYNC_LOCATION_SET, handleSyncLocation);
    GatewayService.on(GatewayEvents.ACTION_TOGGLE_STICK, handleGatewayToggleStick);
    GatewayService.on(GatewayEvents.ACTION_TOGGLE_CAMERA_SYNC, handleGatewayToggleCameraSync);

    return () => {
      // remove listeners
      Socket.off(SocketEvents.SYNC_UPDATE_STATE, handleStateSync);
      // disconnect socket
      SocketService.removeActiveInstance();

      GatewayService.off(GatewayEvents.SYNC_LOCATION_SET, handleSyncLocation);
      GatewayService.off(GatewayEvents.ACTION_TOGGLE_STICK, handleGatewayToggleStick);
      GatewayService.off(GatewayEvents.ACTION_TOGGLE_CAMERA_SYNC, handleGatewayToggleCameraSync);
    };
  }, [Socket, browserHistory, search, boundActions]);

  //// Navigation and story toggle synchronization part

  // presenter part
  useEffect(() => {
    GatewayService.emit(GatewayEvents.SYNC_LOCATION_UPDATE, { value: pathname });
    if (PermissionService.hasRoles([Roles.ROLE_PRESENTER]) && Socket) {
      if (state.isInitialUpdate) {
        setState({ isInitialUpdate: false });
        return;
      }

      Socket.emit(SocketEvents.ACTION_NAVIGATE, {
        value: pathname,
      });
    }
  }, [Socket, pathname]);

  // useEffect(() => {
  //   if (isMacLike()) {
  //     gl.toneMapping = NoToneMapping;
  //     gl.outputEncoding = LinearEncoding;
  //   }
  // }, [gl]);

  const secondarySpaceData = useMemo(
    () => ({
      ...prevSpace,
      isPhoto: true,
    }),
    [prevSpace]
  );

  useEffect(() => {
    const copy = { ...secondarySpaceData };
    delete copy.isPhoto;
    if (isEmpty(copy)) {
      boundActions.setSpace(currSpace);
    }
  }, [secondarySpaceData, boundActions, currSpace]);

  useEffect(() => {
    const canvasEl = gl.domElement;

    const handleEnterVr = () => {
      mainRef.current?.position.setY(1.5);

      try {
        const elm = document.querySelector('.cp360-viewer-frame') as any;
        elm.style.zIndex = 9;
      } catch {}
    };
    const handleExitVr = () => {
      mainRef.current?.position.setY(0);

      try {
        const elm = document.querySelector('.cp360-viewer-frame') as any;
        elm.style.zIndex = 0;
      } catch {}
    };

    canvasEl.addEventListener('custom-enter-vr', handleEnterVr);
    canvasEl.addEventListener('custom-exit-vr', handleExitVr);

    return () => {
      canvasEl.removeEventListener('custom-enter-vr', handleEnterVr);
      canvasEl.removeEventListener('custom-exit-vr', handleExitVr);
    };
  }, [gl, mainRef]);

  let rotation: Euler;
  if (spotLookAt) rotation = new Euler(degToRad(spotLookAt.x), degToRad(spotLookAt.y), 0, 'YXZ');
  else if (lookAt) rotation = new Euler(degToRad(lookAt.x), degToRad(lookAt.y), 0, 'YXZ');
  else rotation = new Euler(0, 0, 0, 'YXZ');

  return (
    <context.Provider
      value={{
        controls: controlsRef,
      }}>
      <group ref={mainRef}>
        <LookControls ref={controlsRef} />
        <group position={[0, 0, 0]}>
          <PerspectiveCamera makeDefault args={[75]}>
            <group visible={showLoader || !spaceLoaded}>
              <Loader scale={[0.1, 0.1, 0.1]} position={[0, 0, -0.2]} />
            </group>
            {!spaceLoaded ? (
              <group ref={loaderSpcContRef.refFn}>
                <LoaderSpace
                  data={secondarySpaceData}
                  radius={0.5}
                  renderOrder={10}
                  enableAnimation
                  ref={loaderSpcRef.refFn}
                  onLoadStart={() => {
                    // console.log('log: push onLoadStart', Date.now());
                    loaderSpcContRef.push(() => {
                      if (!loaderSpcContRef.current) return;
                      // console.log('log: debug: onLoadStart');
                      const x = controlsRef.current?.pitchObject.rotation.x || 0;
                      const y = controlsRef.current?.yawObject.rotation.y || 0;
                      const deltaOfY = secondarySpaceData.box ? 0 : degToRad(90);

                      if (deltaOfY !== 0) {
                        loaderSpcContRef.current.scale.set(-1, 1, 1);
                      } else {
                        loaderSpcContRef.current.scale.set(1, 1, 1);
                      }

                      loaderSpcContRef.current.rotation.set(-x, -(y + deltaOfY), 0, 'ZXY');
                    });
                  }}
                  onLoad={() => {
                    setTimeout(() => {
                      // console.log('log: debug: onLoad');
                      controlsRef.current?.lookAt(rotation);
                      boundActions.setSpace(currSpace);
                    }, 500);
                  }}
                  onAnimComplete={() => {
                    setTimeout(() => {
                      // console.log('log: debug: onAnimComplete');
                      boundActions.setLoadedSpace(currSpace);
                    }, 500);
                  }}
                />
              </group>
            ) : null}
          </PerspectiveCamera>
        </group>

        <Space
          data={newSpace}
          radius={45}
          renderOrder={-1}
          onLoad={() => {
            // console.log('log: push onAnimStart', Date.now());
            loaderSpcRef.push(() => {
              if (!loaderSpcRef.current) return;
              // console.log('log: debug: onAnimStart');
              loaderSpcRef.current.animReset();
              loaderSpcRef.current.animPlay();
            });
          }}
        />

        {coordsSelectEnabled ? <CoordsSelectSpace /> : null}

        {isStickEnabled ? <Stick /> : null}
        <group visible={isContentVisible}>{children}</group>
      </group>
    </context.Provider>
  );
};

export default MainEnvironment;
